import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, withAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import AttachmentsButton from "@/components/AttachmentsButton";
import { usePermissions } from "@/lib/permissions";
import CustomerProfilePanel from "@/components/orders/CustomerProfilePanel";
import QuickProductDialog from "@/components/orders/QuickProductDialog";
import ItemsTable from "@/components/orders/ItemsTable";

export default function Quotations() {
  const { can } = usePermissions();
  const { user } = useAuth();
  const canViewProducts = can("inventory.view");
  const canEditCustomer = can("customers.edit");
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [team, setTeam] = useState([]);
  const [units, setUnits] = useState([]);
  const [org, setOrg] = useState({});
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [items, setItems] = useState([]);
  const [assignedTo, setAssignedTo] = useState("");
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [quickCreateFor, setQuickCreateFor] = useState(null);

  const load = async () => {
    const safe = (p) => p.catch(() => ({ data: [] }));
    const [q, c, p, o, t, u] = await Promise.all([
      safe(api.get("/quotations")),
      safe(api.get("/customers", { params: { scope_mode: "tenant" } })),
      canViewProducts ? safe(api.get("/products")) : Promise.resolve({ data: [] }),
      safe(api.get("/organization")),
      safe(api.get("/users")),
      safe(api.get("/lookups/product_unit")),
    ]);
    setQuotes(q.data); setCustomers(c.data); setProducts(p.data);
    setOrg(o.data || {}); setTeam(t.data); setUnits(u.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const addItem = () => setItems([...items, { product_id: "", name: "", qty: 1, price: 0, gst: 18, hsn: "", unit: "PCS" }]);
  const resetForm = () => { setCustomerId(""); setItems([]); setDiscount(0); setNotes(""); setAssignedTo(""); };

  const subtotal = items.reduce((s, it) => s + it.qty * it.price, 0);
  const gst = items.reduce((s, it) => s + it.qty * it.price * ((it.gst || 0) / 100), 0);
  const total = subtotal + gst - (+discount || 0);

  const create = async () => {
    const cust = customers.find(c => c.id === customerId);
    if (!cust || !items.length) return toast.error("Pick customer & add items");
    try {
      await api.post("/quotations", {
        customer_id: cust.id, customer_name: cust.customer_name, items,
        discount: +discount || 0, notes, status: "draft",
        assigned_to: assignedTo || null,
      });
      toast.success("Quotation created");
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const printQ = (q) => {
    const line = (label, value) => value ? `<div>${label}: <strong>${value}</strong></div>` : "";
    const social = [
      org.website && `<a href="${org.website}">${org.website.replace(/^https?:\/\//, "")}</a>`,
      org.primary_email && `<a href="mailto:${org.primary_email}">${org.primary_email}</a>`,
      org.primary_mobile,
    ].filter(Boolean).join(" · ");
    const address = [org.billing_address, org.city, org.state, org.pincode, org.country].filter(Boolean).join(", ");
    const logoSrc = org.logo ? withAuth(org.logo) : "";
    const sigSrc = org.signature ? withAuth(org.signature) : "";
    const logoHtml = logoSrc ? `<img src="${logoSrc}" alt="logo" style="max-height:56px;max-width:200px;object-fit:contain"/>` : "";
    const brand = org.brand_name || org.name || "Your Company";
    const currency = "₹";
    const html = `<html><head><title>${q.number}</title><style>
    body{font-family:'Helvetica Neue',Arial,sans-serif;padding:40px;max-width:820px;margin:auto;color:#222}
    h1{color:#4a2f1e;margin:0 0 4px 0;font-size:26px}
    table{width:100%;border-collapse:collapse;margin-top:24px}
    th,td{border-bottom:1px solid #e5dcc9;padding:10px;text-align:left;font-size:13px}
    th{background:#faf6ee;text-transform:uppercase;font-size:11px;letter-spacing:0.05em;color:#7b6b5a}
    .right{text-align:right}
    .muted{color:#7b6b5a;font-size:12px}
    .footer{margin-top:32px;padding-top:16px;border-top:1px solid #e5dcc9;font-size:11px;color:#7b6b5a}
    .sig{margin-top:32px;display:flex;justify-content:space-between;align-items:end;gap:32px}
    .box{padding:14px 18px;background:#faf6ee;border-radius:8px;margin-top:12px}
    @media print { @page { margin: 12mm; } }
    </style></head><body onload="setTimeout(()=>window.print(),200)">
    <div style="display:flex;justify-content:space-between;align-items:start;gap:24px">
      <div>
        ${logoHtml}
        <h1 style="margin-top:${org.logo ? "12px" : "0"}">${brand}</h1>
        ${org.description ? `<div class="muted" style="max-width:380px">${org.description}</div>` : ""}
        <div class="muted" style="margin-top:6px">${address}</div>
        <div class="muted">${social}</div>
        ${line("GST", org.gst_number)}
      </div>
      <div class="right">
        <div style="font-size:22px;font-weight:700;color:#4a2f1e">Quotation</div>
        <div class="muted"># ${q.number}</div>
        <div class="muted">${new Date(q.created_at).toLocaleDateString("en-IN")}</div>
      </div>
    </div>
    <div class="box"><strong>Bill To</strong><div style="margin-top:4px">${q.customer_name}</div></div>
    <table>
      <thead><tr><th>Item</th><th>Qty</th><th>Unit</th><th>Price</th><th>GST %</th><th class="right">Amount</th></tr></thead>
      <tbody>
      ${q.items.map(i => `<tr><td>${i.name}</td><td>${i.qty}</td><td>${i.unit || "PCS"}</td><td>${currency}${i.price}</td><td>${i.gst}%</td><td class="right">${currency}${(i.qty * i.price).toFixed(2)}</td></tr>`).join("")}
      </tbody>
    </table>
    <div style="margin-top:20px" class="right">
      <div>Subtotal: ${currency}${q.subtotal?.toFixed(2)}</div>
      <div>GST: ${currency}${q.gst_amount?.toFixed(2)}</div>
      ${q.discount ? `<div>Discount: -${currency}${q.discount?.toFixed(2)}</div>` : ""}
      <div style="font-size:20px;font-weight:bold;margin-top:8px;color:#4a2f1e">Total: ${currency}${q.total?.toFixed(2)}</div>
    </div>
    ${q.notes ? `<div class="box" style="margin-top:20px"><strong>Notes</strong><div>${q.notes}</div></div>` : ""}
    <div class="sig">
      <div class="muted">Thank you for your business.</div>
      ${org.signature ? `<div style="text-align:right"><img src="${sigSrc}" alt="signature" style="max-height:60px"/><div class="muted" style="margin-top:4px">Authorised Signatory</div></div>` : ""}
    </div>
    <div class="footer">${brand}${org.pan_number ? ` · PAN ${org.pan_number}` : ""}${org.cin_number ? ` · CIN ${org.cin_number}` : ""}</div>
    </body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) toast.error("Popup blocked — allow popups to print quotations");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="overline text-muted-foreground mb-2">Proposals</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight leading-none">Quotations</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          {can("quotations.create") && (
            <DialogTrigger asChild><Button className="rounded-full" data-testid="add-quote-button"><Plus size={16} className="mr-2" />New Quote</Button></DialogTrigger>
          )}
          <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Quotation</DialogTitle></DialogHeader>
            <div className="grid md:grid-cols-[1fr_320px] gap-4">
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-xs">CUSTOMER</Label>
                  <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger data-testid="quote-customer-select"><SelectValue placeholder="Pick customer" /></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between"><Label className="text-xs">ITEMS</Label><Button size="sm" variant="outline" onClick={addItem} data-testid="quote-add-item"><Plus size={12} className="mr-1" />Item</Button></div>
                  <ItemsTable items={items} products={products} units={units}
                    onChange={setItems} testidPrefix="quote-line"
                    onQuickCreate={(idx) => setQuickCreateFor(idx)} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">ASSIGN TO</Label>
                    <Select value={assignedTo || "__none__"} onValueChange={(v) => setAssignedTo(v === "__none__" ? "" : v)}>
                      <SelectTrigger data-testid="quote-assigned-to"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {team.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">DISCOUNT ₹</Label><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="quote-discount" /></div>
                  <div>
                    <Label className="text-xs">CREATED BY</Label>
                    <div className="h-10 px-3 flex items-center gap-2 rounded-md border border-border bg-secondary/40 text-sm text-muted-foreground" data-testid="quote-created-by">
                      <User size={12} weight="duotone" />
                      <span className="truncate">{user?.name || user?.email}</span>
                      <span className="ml-auto text-[10px] uppercase">auto</span>
                    </div>
                  </div>
                </div>
                <Input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quote-notes" />
                <div className="text-right" data-testid="quote-total">
                  <div className="text-xs text-muted-foreground">Subtotal: ₹{subtotal.toFixed(2)} + GST ₹{gst.toFixed(2)}</div>
                  <div className="font-heading font-bold text-xl">Total: ₹{total.toFixed(2)}</div>
                </div>
              </div>
              <div className="md:sticky md:top-2 md:h-fit">
                <CustomerProfilePanel customerId={customerId} canEditCustomer={canEditCustomer} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} data-testid="save-quote-button">Create</Button>
            </DialogFooter>
            <QuickProductDialog
              open={quickCreateFor !== null}
              onOpenChange={(v) => !v && setQuickCreateFor(null)}
              units={units}
              onCreated={(p) => {
                setProducts(prev => [p, ...prev]);
                setItems(items.map((it, idx) => idx === quickCreateFor
                  ? { ...it, product_id: p.id, name: p.name, price: p.price, gst: p.gst, unit: p.unit || "PCS" }
                  : it));
                setQuickCreateFor(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground tracking-wider">
              <tr><th className="text-left p-4">Number</th><th className="text-left p-4">Customer</th><th className="text-left p-4">Items</th><th className="text-left p-4">Total</th><th className="text-left p-4">Status</th><th className="text-left p-4">Assigned</th><th className="text-left p-4">Created By</th><th className="text-left p-4">Date</th><th></th></tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} className="border-t border-border/40" data-testid={`quote-${q.id}`}>
                  <td className="p-4 font-mono text-xs">{q.number}</td>
                  <td className="p-4 font-medium">{q.customer_name}</td>
                  <td className="p-4 text-muted-foreground">{q.items?.length} items</td>
                  <td className="p-4 font-heading font-bold">₹{q.total?.toLocaleString("en-IN")}</td>
                  <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-secondary">{q.status}</span></td>
                  <td className="p-4 text-xs" data-testid={`quote-assigned-${q.id}`}>{q.assigned_to_name || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-4 text-xs text-muted-foreground" data-testid={`quote-created-by-${q.id}`}>{q.created_by_name || "—"}</td>
                  <td className="p-4 text-xs text-muted-foreground">{new Date(q.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="p-4"><div className="flex items-center gap-1"><Button size="sm" variant="outline" onClick={() => printQ(q)}>PDF</Button><AttachmentsButton entityType="quotation" entityId={q.id} label={`Files — ${q.number}`} testId={`attach-quote-${q.id}`} /></div></td>
                </tr>
              ))}
              {!quotes.length && <tr><td colSpan={9} className="p-10 text-center text-muted-foreground">No quotations yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
